import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Calendar, ArrowLeft } from "@phosphor-icons/react";

const STATUS_TABS = [
  { k: "pending",  lbl: "Pending",  tone: "warning" },
  { k: "approved", lbl: "Approved", tone: "success" },
  { k: "rejected", lbl: "Rejected", tone: "danger"  },
];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function LeaveApprovals() {
  const [tab, setTab] = useState("pending");
  const [leaves, setLeaves] = useState([]);
  const [busy, setBusy] = useState(null);
  const [comment, setComment] = useState({});

  const load = () => api.get(`/hr/team/leaves?status=${tab}`).then(r => setLeaves(r.data.leaves || [])).catch(() => setLeaves([]));
  useEffect(() => { load(); }, [tab]);

  const decide = async (leave_id, status) => {
    setBusy(leave_id);
    try {
      await api.post(`/hr/leave-requests/${leave_id}/decide`, { status, comment: comment[leave_id] || "" });
      load();
      setComment(c => ({ ...c, [leave_id]: "" }));
    } catch (e) {
      alert("Decision failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="leave-approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">HR · Manager view</div>
          <h1 className="font-display font-bold text-3xl mt-1 text-[var(--text-primary)]">Leave Approvals</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">Requests from your direct reports.</div>
        </div>
        <Link to="/hr/leave" className="span-btn span-btn-ghost" data-testid="back-to-leave">
          <ArrowLeft size={14} /> My Leave
        </Link>
      </div>

      <div className="flex gap-1">
        {STATUS_TABS.map(t => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-1.5 text-[12px] rounded transition ${tab === t.k ? "bg-[var(--brand-primary)] text-white" : "bg-[var(--surface-2)] text-[var(--text-secondary)]"}`}>
            {t.lbl} {tab === t.k && `(${leaves.length})`}
          </button>
        ))}
      </div>

      <div className="bg-[var(--surface,#fff)] border border-[var(--border)] rounded p-5">
        {leaves.length === 0 ? (
          <div className="text-[12px] text-[var(--text-muted)] text-center py-6" data-testid="approvals-empty">
            No {tab} requests.
          </div>
        ) : (
          <div className="space-y-3">
            {leaves.map(lr => (
              <div key={lr.leave_id} className="border border-[var(--border)] rounded p-3" data-testid={`approval-row-${lr.leave_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar size={14} className="text-[var(--brand-primary)]" />
                      <span className="font-bold text-[var(--text-primary)]">{lr.user_name}</span>
                      <span className="font-mono text-[10px] px-1.5 bg-[var(--surface-2)] rounded">{lr.user_initials}</span>
                    </div>
                    <div className="text-[12px]">
                      <span className="font-semibold">{lr.leave_type}</span> · {fmtDate(lr.start_date)} → {fmtDate(lr.end_date)} · <span className="font-mono">{lr.days} day(s)</span>
                    </div>
                    {lr.reason && <div className="text-[11px] text-[var(--text-secondary)] mt-1">&quot;{lr.reason}&quot;</div>}
                    <div className="text-[10px] text-[var(--text-muted)] mt-1">Submitted {fmtDate(lr.submitted_at)}</div>
                  </div>
                  {tab === "pending" ? (
                    <div className="flex flex-col gap-2 items-end">
                      <input
                        data-testid={`comment-${lr.leave_id}`}
                        placeholder="Optional comment"
                        value={comment[lr.leave_id] || ""}
                        onChange={e => setComment(c => ({ ...c, [lr.leave_id]: e.target.value }))}
                        className="span-input text-[11px] w-[200px]"
                      />
                      <div className="flex gap-1.5">
                        <button
                          data-testid={`approve-${lr.leave_id}`}
                          disabled={busy === lr.leave_id}
                          onClick={() => decide(lr.leave_id, "approved")}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[var(--status-success)] text-white text-[11px] rounded hover:opacity-90 disabled:opacity-50">
                          <CheckCircle size={12} weight="fill" /> Approve
                        </button>
                        <button
                          data-testid={`reject-${lr.leave_id}`}
                          disabled={busy === lr.leave_id}
                          onClick={() => decide(lr.leave_id, "rejected")}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[var(--status-danger)] text-white text-[11px] rounded hover:opacity-90 disabled:opacity-50">
                          <XCircle size={12} weight="fill" /> Reject
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-right text-[10px]">
                      <div className={`uppercase font-bold ${lr.status === "approved" ? "text-[var(--status-success)]" : "text-[var(--status-danger)]"}`}>{lr.status}</div>
                      {lr.decision_comment && <div className="text-[var(--text-muted)] mt-0.5 max-w-[220px]">&quot;{lr.decision_comment}&quot;</div>}
                      <div className="text-[var(--text-muted)] mt-0.5">{fmtDate(lr.decided_at)}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
